import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useLangMap } from '../hooks/useLanguages';
import { loadSpeechComponents } from '../services/dataApi';
import { QuestionCard } from '../pages/exercise/ExercisePage';
import { ReadingMaterial, ConversationMaterial, SectionMaterial, TableMaterial, topicQuestion } from '../pages/journeys/TopicLessonPage';
import { ChunkyButton, Confetti, FeedbackDrawer, GameProgressBar } from './GameUI';

const PLAYER_COLOR = '#7C3AED';

// Shared interactive lesson-card flow (intro/recap/table/conversation/section/mcq +
// progress bar + finish screen) used by topic-lessons, grammar,
// common-foundations, and alphabet — each just supplies a `loader` that
// resolves to { chapter, chapters, items } in this same numbered-card shape.
// `onComplete` is optional (defaults to a no-op) — only topic-lessons writes
// real per-chapter progress; the others have no chapter catalog to track
// against.
export default function LessonFlow({ loader, loaderDeps, backTo, backLabel, onComplete, onBack }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = onBack || (() => navigate(backTo));
  const { languagePrefs } = useAuth();
  const { langMap } = useLangMap();
  const targetCode = languagePrefs?.targetLanguage || '';
  const nativeCode = languagePrefs?.nativeLanguage || 'en';
  const [lesson, setLesson] = useState(null);
  const [status, setStatus] = useState('loading');
  const [index, setIndex] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [finished, setFinished] = useState(false);
  const [speechComponents, setSpeechComponents] = useState(null);

  useEffect(() => {
    let active = true;
    loadSpeechComponents(nativeCode).then((data) => { if (active) setSpeechComponents(data); }).catch(() => {});
    return () => { active = false; };
  }, [nativeCode]);

  useEffect(() => {
    let active = true;
    setStatus('loading');
    setLesson(null);
    setIndex(0);
    setFeedback(null);
    setFinished(false);
    loader()
      .then((loaded) => {
        if (!active) return;
        setLesson(loaded);
        setStatus(loaded.items.length ? 'ready' : 'unavailable');
      })
      .catch(() => { if (active) setStatus('unavailable'); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, loaderDeps);

  const items = lesson?.items || [];
  const item = items[index];
  const targetDir = langMap[targetCode]?.dir || 'ltr';
  const nativeDir = langMap[nativeCode]?.dir || 'ltr';
  const ttsCode = langMap[targetCode]?.tts || targetCode;

  const completeLesson = () => {
    if (lesson && onComplete) onComplete(lesson);
    setFinished(true);
  };

  const continueLesson = () => {
    setFeedback(null);
    if (index + 1 >= items.length) completeLesson();
    else setIndex((current) => current + 1);
  };

  if (status === 'loading') {
    return <div className="page-shell"><div className="h-72 animate-pulse rounded-3xl bg-violet-100" aria-label={t('journeyPages.lessonLoading')} /></div>;
  }

  if (status === 'unavailable' || !lesson) {
    return (
      <div className="page-shell text-center">
        <div className="rounded-3xl border border-rose-200 bg-white p-10 shadow-sm">
          <span className="text-5xl" aria-hidden="true">📕</span>
          <h1 className="mt-4 text-2xl font-black text-slate-900">{t('journeyPages.lessonUnavailable')}</h1>
          <button type="button" onClick={goBack} className="mt-5 inline-flex rounded-xl bg-violet-600 px-5 py-3 text-sm font-black text-white">{backLabel}</button>
        </div>
      </div>
    );
  }

  if (finished) {
    return (
      <div className="relative grid min-h-[calc(100vh-6rem)] place-items-center bg-gradient-to-br from-violet-50 to-emerald-50 px-4 text-center">
        <Confetti />
        <section className="relative w-full max-w-lg rounded-3xl border border-emerald-200 bg-white p-8 shadow-xl sm:p-10">
          <span className="text-7xl" aria-hidden="true">🏆</span>
          <h1 className="mt-5 text-3xl font-black text-slate-900">{t('journeyPages.lessonFinished')}</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">{t('journeyPages.lessonFinishedDesc')}</p>
          <ChunkyButton color="#10B981" onClick={goBack} className="mt-7 w-full py-4 text-white">{backLabel}</ChunkyButton>
        </section>
      </div>
    );
  }

  const isReading = item.type === 'intro' || item.type === 'recap';
  const isConversation = item.type === 'conversation';
  const isTable = item.type === 'table';
  const isSection = item.type === 'section';
  const question = !isReading && !isConversation && !isTable && !isSection ? topicQuestion(item, null) : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white">
      <header className="border-b border-violet-100 bg-white/90 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <button type="button" onClick={goBack} className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-xl text-slate-400 transition hover:bg-slate-100" aria-label={backLabel}>✕</button>
          <div className="min-w-0 flex-1">
            <GameProgressBar value={items.length ? index / items.length : 0} color={PLAYER_COLOR} />
            <p className="mt-1 truncate text-[10px] font-black uppercase tracking-wider text-violet-500">{lesson.chapter.label}</p>
          </div>
          <span className="shrink-0 rounded-full bg-violet-100 px-3 py-1.5 text-xs font-black text-violet-700">{index + 1}/{items.length}</span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-7" style={{ paddingBottom: feedback ? 180 : 28 }}>
        {isReading && <ReadingMaterial item={item} imageUrl={null} audioUrl={null} speechComponents={speechComponents} ttsCode={ttsCode} t={t} />}
        {isConversation && <ConversationMaterial item={item} t={t} />}
        {isTable && <TableMaterial item={item} />}
        {isSection && <SectionMaterial item={item} t={t} />}
        {question && (
          <QuestionCard
            key={`${lesson.chapter.id}-${index}`}
            question={question}
            langCode={targetCode}
            ttsCode={ttsCode}
            targetDir={targetDir}
            nativeDir={nativeDir}
            color={PLAYER_COLOR}
            onMarked={(correct, correctAnswer, explanation) => setFeedback({ correct, correctAnswer, explanation })}
            t={t}
          />
        )}

        {(isReading || isConversation || isTable || isSection) && (
          <ChunkyButton color={PLAYER_COLOR} onClick={continueLesson} className="mt-5 w-full py-4 text-white">
            {index + 1 >= items.length ? t('journeyPages.finishLesson') : t('game.continue')}
          </ChunkyButton>
        )}
      </main>

      {feedback && (
        <FeedbackDrawer
          correct={feedback.correct}
          title={feedback.correct ? t('game.praises', { returnObjects: true })[0] : t('game.incorrectTitle')}
          subtitle={feedback.correct ? feedback.explanation : feedback.correctAnswer ? t('game.correctAnswerLabel', { answer: feedback.correctAnswer }) : feedback.explanation}
          continueLabel={index + 1 >= items.length ? t('journeyPages.finishLesson') : t('game.continue')}
          onContinue={continueLesson}
        />
      )}
    </div>
  );
}
